import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useLocation, useHistory } from 'react-router-dom'
import { MenuIcon } from '@heroicons/react/outline'

import { useGlobalState } from '@/state'
import useDropdown from '@/hooks/useDropdown'
import classNames from '@/utils/classNamesLocal'

// One list drives both the tab row and the phone menu.
const TABS = (tabs) => [
  { name: 'Dashboard', path: '' },
  { name: 'Edit', path: '/edit' },
  { name: 'Roles', path: '/roles', count: tabs.rolesCount },
  { name: 'Submissions', path: '/submissions', count: tabs.submissionsCount },
  { name: 'DMs', path: '/messages', count: tabs.messageCount },
  { name: 'Team', path: '/team' },
  { name: 'Analytics', path: '/analytics' }
]

const Count = ({ number, selected }) => {
  if (!number || parseInt(number, 10) <= 0) { return null }
  return <span className={classNames('ml-2 rounded-full px-2 py-0.5 text-xs font-semibold', selected ? 'bg-cccblue text-white' : 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100')}>{number}</span>
}

const Tab = ({ url, name, count, mobile, onNavigate }) => {
  const location = useLocation()
  const history = useHistory()
  const selected = location.pathname === url
  const go = () => { if (onNavigate) { onNavigate() } history.push(url) }

  if (mobile) {
    return <button type='button' onClick={go} aria-current={selected ? 'page' : undefined}
      className={classNames('flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-base font-medium', selected ? 'bg-cccblue/10 text-cccblue' : 'text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-700')}>
      {name}<Count number={count} selected={selected} />
    </button>
  }
  return <button type='button' onClick={go} aria-current={selected ? 'page' : undefined}
    className={classNames('whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cccblue rounded-t-md',
      selected ? 'border-cccblue text-cccblue' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white')}>
    {name}<Count number={count} selected={selected} />
  </button>
}

// Phone-only menu button that lives in the page title row.
export function MobileTabsMenu () {
  const [manageProjectContainer] = useGlobalState('manageProjectContainer')
  const { tabs } = manageProjectContainer
  const dropdownRef = useRef(false)
  const { dropdownOpen, setDropdownOpen, openDropdown } = useDropdown(dropdownRef)

  if (!tabs?.projectId) { return null }
  const base = `/manage/projects/${tabs.projectId}`

  return <div className='relative md:hidden'>
    <button type='button' onClick={openDropdown} aria-label='Project menu' className='inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-gray-900 shadow-sm hover:bg-gray-100 focus:ring-2 focus:ring-cccpurple focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800'>
      <MenuIcon className='h-5 w-5' aria-hidden='true' />
    </button>
    { dropdownOpen && <div ref={dropdownRef} tabIndex='-1' className='absolute right-0 z-30 mt-1 min-w-[14rem] rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800'>
      { TABS(tabs).map((t) => <Tab key={t.path} mobile name={t.name} count={t.count} url={`${base}${t.path}`} onNavigate={() => setDropdownOpen(false)} />) }
    </div> }
  </div>
}

// Tab row from the md breakpoint up. Scrolls sideways if it has to rather
// than hiding behind a hamburger on tablets.
export default function ManageProjectsTabs () {
  const [manageProjectContainer] = useGlobalState('manageProjectContainer')
  const { tabs } = manageProjectContainer
  const base = `/manage/projects/${tabs.projectId}`

  return <div className='hidden border-b border-gray-200 dark:border-gray-700 md:block'>
    <nav className='-mb-px flex gap-x-1 overflow-x-auto' aria-label='Project sections' style={{ scrollbarWidth: 'thin' }}>
      { TABS(tabs).map((t) => <Tab key={t.path} name={t.name} count={t.count} url={`${base}${t.path}`} />) }
    </nav>
  </div>
}

Tab.propTypes = {
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  count: PropTypes.number,
  mobile: PropTypes.bool,
  onNavigate: PropTypes.func
}
